import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ResponseStatus } from '@/types';
import { ITaskCompletionDataResponse } from '@/types/tasks';

import { useGetTask } from '../tasks';

interface TaskHandlerProps {
  taskId?: string;
  refetchInterval?: number;
  onStatusSuccess: (response?: any) => void;
  onStatusFailure?: (errorData: ITaskCompletionDataResponse) => void;
  onCompleted?: () => void;
}

export const useTaskHandler = ({
  onStatusSuccess,
  onCompleted,
  onStatusFailure,
  refetchInterval,
  taskId,
}: TaskHandlerProps) => {
  const [isPending, setIsPending] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const taskIdFromSearchParams = useRef(searchParams.get('taskId'));

  const isTaskCompleted = useMemo(
    () => isCompleted && !taskId && !taskIdFromSearchParams.current,
    [isCompleted, taskId]
  );

  const { data: taskData, isFetching } = useGetTask(
    taskId ? taskId : taskIdFromSearchParams.current || '',
    isTaskCompleted,
    refetchInterval
  );

  const handleTaskData = useCallback(() => {
    if (!taskData) return;

    const deleteTaskIdFromSearchParams = () => {
      if (searchParams.has('taskId')) {
        taskIdFromSearchParams.current = '';
        searchParams.delete('taskId');
        setSearchParams(searchParams);
      }
    };

    const handleCompleted = () => {
      setIsCompleted(true);
      deleteTaskIdFromSearchParams();
      if (onCompleted) onCompleted();
    };

    const {
      data: { status },
    } = taskData;
    if (status === ResponseStatus.SUCCESS) onStatusSuccess();

    if (status === ResponseStatus.FAILURE && onStatusFailure)
      onStatusFailure(taskData?.data);

    setIsPending(status === ResponseStatus.PENDING);
    setIsError(status === ResponseStatus.FAILURE);
    if (status !== ResponseStatus.PENDING) {
      handleCompleted();
    }
  }, [taskData, onStatusFailure, onStatusSuccess]);

  useEffect(() => {
    handleTaskData();
  }, [taskData, isFetching, handleTaskData]);

  const isLoading = useMemo(
    () => isPending || isFetching,
    [isPending, isFetching]
  );

  return {
    isLoading,
    isPending,
    isCompleted: isTaskCompleted,
    isError,
    taskId: taskId || taskIdFromSearchParams.current,
  };
};
